import helpers from '../../utils/helpers';
import log from '../logger/log';

interface IFramesQueueItem {
    frames: string[];
    onSuccess: () => void;
    onError: () => void;
    timeout: number;
    skipErrorOnTimeout?: boolean;
    isDebug: boolean;
    responseTimeoutMs?: number;
    retryCount?: number;
    doNotSplit?: boolean;
}

interface IFramesQueueParams extends IFramesQueueItem {
    processImmediately: boolean;
    processFirst: boolean;
}

export interface IStateBase {
    currentFrame?: string;
    framesToResponse: string[];
    onError?: () => void;
    onSuccess?: (responses: string[]) => void;
    responses: string[];
    retryCount?: number;
    retryNumber: number;
    timeout: number;
    skipErrorOnTimeout?: boolean;
    wrongHeaderRetryCount: number;
    isDebug: boolean;
    responseTimeoutMs?: number;
}

export default abstract class CommunicatorClientBase<T, K extends IStateBase> {
    protected isActive = false;
    protected framesQueue: IFramesQueueItem[] = [];
    protected responseTimeout?: ReturnType<typeof setTimeout>;
    protected iqosClient!: T;
    protected state: K = this.getInitialState();

    abstract getInitialState(): K;
    abstract writeFrames(): void;
    abstract writeFrame: (frame: string, isExtendedFrame: boolean) => void;

    addFramesToQueue({
        frames,
        onSuccess,
        onError,
        processImmediately = true,
        processFirst,
        timeout,
        skipErrorOnTimeout,
        isDebug,
        responseTimeoutMs,
        retryCount,
        doNotSplit,
    }: IFramesQueueParams): void {
        if (isDebug) {
            log.debug(`CommunicatorClientBase: add frames to queue, frames: ${JSON.stringify(frames)}`);
        }

        const queueItem = {
            frames,
            onSuccess,
            onError,
            timeout,
            skipErrorOnTimeout,
            isDebug,
            responseTimeoutMs,
            retryCount,
            doNotSplit,
        };

        if (processFirst) {
            this.framesQueue = [queueItem, ...this.framesQueue];
        } else {
            this.framesQueue.push(queueItem);
        }

        if (processImmediately) {
            this.processQueue();
        }
    }

    restartListeners = (): void => undefined;

    protected resetRetries(): void {
        this.state.retryNumber = 0;
    }

    protected resetState(): void {
        this.state = this.getInitialState();
    }

    protected processQueue = (): void => {
        if (!this.state.framesToResponse.length) {
            this.processFrames();
        }
    };

    protected processFrames = (): void => {
        this.resetState();

        if (!this.framesQueue.length) return;

        const {
            frames,
            onSuccess,
            onError,
            timeout,
            skipErrorOnTimeout,
            isDebug,
            responseTimeoutMs,
            retryCount,
            doNotSplit,
        } = this.framesQueue.shift()!;

        if (isDebug) {
            log.debug(`CommunicatorClientBase: process frames: ${JSON.stringify(frames)}`);
        }

        this.state = {
            ...this.state,
            framesToResponse: frames,
            onSuccess,
            onError,
            timeout,
            skipErrorOnTimeout,
            isDebug,
            responseTimeoutMs,
            retryCount,
            doNotSplit,
        };

        this.writeFrames();
    };

    protected onFrameResponseError = (): void => {
        const {onError} = this.state;

        this.clearResponseTimeout();
        helpers.runFunction(onError);
        this.processFrames();
    };

    protected clearResponseTimeout(): void {
        clearTimeout(this.responseTimeout!);
    }

    protected onFramesResponse(): void {
        const {responses, onSuccess} = this.state;

        if (onSuccess) {
            const filteredResponses = responses.filter((response) => response !== '');

            onSuccess(filteredResponses);
        }

        this.processFrames();
    }

    protected clearQueue(): void {
        helpers.runFunction(this.state.onError, true);
        this.resetState();

        this.framesQueue.forEach((frameQueue) => {
            const {onError} = frameQueue;

            helpers.runFunction(onError, true);
        });
        this.framesQueue = [];
    }
}
